import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import type { AssistantSkillModelData } from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import type { IUpdateAssistantSkillOperation } from "@/store/sync/operations/types";
import type { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

const hasPayloadField = (
  payload: IUpdateAssistantSkillOperation["payload"],
  field: keyof IUpdateAssistantSkillOperation["payload"]
) => Object.prototype.hasOwnProperty.call(payload, field);

export class UpdateAssistantSkillOperation extends BaseSyncOperation<IUpdateAssistantSkillOperation> {
  get operationKind(): "UPDATE_ASSISTANT_SKILL" {
    return "UPDATE_ASSISTANT_SKILL";
  }

  override get waitForPullConfirmationBeforeOptimisticCleanup(): boolean {
    return true;
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const existingSkill = await this.store.assistantSkills.getAsync(this.payload.id);
    if (!existingSkill) {
      logger.error({
        message: "Attempted to update assistant skill that does not exist",
        info: { operation: getLoggableOperation(this) },
      });
      return [];
    }

    const modelData = existingSkill.modelData;
    const skillUpdate = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_SKILL",
        model_version: existingSkill.modelVersion,
        model_data: {
          ...modelData,
          name: this.payload.name ?? modelData.name,
          when_to_use: this.payload.when_to_use ?? modelData.when_to_use,
          instructions: this.payload.instructions ?? modelData.instructions,
          status: this.payload.status ?? modelData.status,
          public_id:
            hasPayloadField(this.payload, "sharing_enabled") &&
            this.payload.sharing_enabled === false
              ? null
              : modelData.public_id,
          updated_at: this.committedAt,
        },
        model_scopes: existingSkill.modelScopes,
      },
    } as unknown as OptimisticSyncUpdate<AssistantSkillModelData>;

    return [skillUpdate];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantSkills.recompute(this.payload.id);
  }
}
